import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import {
  CalendarClock, Users, Plus, X, Search, Loader2, Check, Unlink, ExternalLink, Repeat, CalendarPlus,
} from 'lucide-react';

const tzOffset = () => -new Date().getTimezoneOffset();

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dateLabel(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((dt - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return `${DOW_SHORT[dt.getDay()]}, ${d} ${MONTHS_SHORT[m - 1]}`;
}

const recurShort = (r) => ({ daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', monthly_weekday: 'Monthly', yearly: 'Yearly' }[r] || '');

const KindIcon = ({ kind, size = 15, className = '' }) =>
  (kind === 'meeting'
    ? <Users size={size} className={className} />
    : <CalendarClock size={size} className={className} />);

// Linked events & meetings section for the task detail panel. Lets the user
// attach the task to any planner event/meeting scheduled today or later and see
// those links (the same links surface on the event/meeting detail in Planner).
export default function TaskLinkedEvents({ taskId, onOpenItem, notify }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await notesApi.get(`/planner/tasks/${taskId}/schedule-links`, { params: { tz_offset: tzOffset() } });
      setLinks(data.links || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { setLoading(true); fetchLinks(); }, [fetchLinks]);

  const unlink = useCallback(async (linkId) => {
    const prev = links;
    setLinks((ls) => ls.filter((l) => l.id !== linkId)); // optimistic
    try {
      await notesApi.delete(`/planner/tasks/${taskId}/schedule-links/${linkId}`);
    } catch {
      setLinks(prev);
      notify?.('Could not unlink. Please try again.');
    }
  }, [taskId, links, notify]);

  return (
    <section data-testid="linked-events-section" className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-[13px] font-semibold text-[var(--n-ink)]">
          <CalendarClock size={15} className="text-[var(--n-accent)]" />
          Events &amp; Meetings
          {links.length > 0 && (
            <span className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--n-muted)]" data-testid="linked-events-count-chip">{links.length}</span>
          )}
        </h3>
        <button type="button" data-testid="task-link-event-button" onClick={() => setPickerOpen(true)}
          className="n-focus inline-flex items-center gap-1 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[12px] font-medium text-[var(--n-accent)] transition-colors hover:bg-[var(--n-accent-bg)]">
          <Plus size={13} /> Link
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-1 text-[12px] text-[var(--n-muted)]"><Loader2 size={13} className="animate-spin" /> Loading…</div>
      ) : links.length === 0 ? (
        <p data-testid="linked-events-empty" className="text-[12px] text-[var(--n-muted)]">Not linked to anything yet. Use <span className="font-medium text-[var(--n-ink-soft)]">Link</span> to attach an event or meeting.</p>
      ) : (
        <div className="flex gap-2.5 overflow-x-auto pb-1 n-scroll" data-testid="linked-events-list">
          {links.map((l) => (
            <div key={l.id} data-testid="linked-event-card" data-target-id={l.target_id}
              className="group/lnk relative flex w-[228px] shrink-0 cursor-pointer flex-col gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas)] p-3 transition-colors hover:border-[var(--n-accent)]"
              role="button" tabIndex={0}
              onClick={() => onOpenItem?.(l.date)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenItem?.(l.date); } }}>
              <div className="flex items-start gap-2">
                <KindIcon kind={l.kind} className="mt-0.5 shrink-0 text-[var(--n-accent)]" />
                <span className="line-clamp-2 flex-1 text-[13px] font-semibold leading-snug text-[var(--n-ink)]" data-testid="linked-event-title">{l.title}</span>
                <ExternalLink size={13} className="shrink-0 text-[var(--n-muted-2)] opacity-0 transition-opacity group-hover/lnk:opacity-100" />
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--n-muted)]">
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 font-medium capitalize text-[var(--n-ink-soft)]">{l.kind}</span>
                <span className="font-medium text-[var(--n-ink-soft)]">{dateLabel(l.date)}</span>
                {l.start_time && <span>· {l.start_time}</span>}
                {l.recurring && <span className="inline-flex items-center gap-0.5 text-[var(--n-accent)]"><Repeat size={10} />{recurShort(l.recurrence)}</span>}
              </div>
              <button type="button" data-testid="linked-event-unlink" title="Unlink"
                onClick={(e) => { e.stopPropagation(); unlink(l.id); }}
                className="n-focus absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-md text-[var(--n-muted-2)] opacity-0 transition-all hover:bg-[var(--n-danger)]/10 hover:text-[var(--n-danger)] group-hover/lnk:opacity-100">
                <Unlink size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <EventPicker taskId={taskId} onClose={() => setPickerOpen(false)}
          onLinked={() => { setPickerOpen(false); fetchLinks(); }} notify={notify} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Event/meeting search + multi-select dialog (today or later only)
// ---------------------------------------------------------------------------
function EventPicker({ taskId, onClose, onLinked, notify }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const debRef = useRef(null);

  const fetchResults = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await notesApi.get(`/planner/tasks/${taskId}/schedule-picker`, { params: { q: term || '', tz_offset: tzOffset() } });
      setResults(data.targets || []);
    } catch { setResults([]); } finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { fetchResults(''); }, [fetchResults]);
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchResults(q), 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, fetchResults]);

  const toggle = (key) => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const submit = useCallback(async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    const targets = Array.from(selected).map((k) => { const [target_type, target_id] = k.split('::'); return { target_type, target_id }; });
    try {
      await notesApi.post(`/planner/tasks/${taskId}/schedule-links`, { targets });
      notify?.(`Linked ${targets.length} item${targets.length > 1 ? 's' : ''}`);
      onLinked?.();
    } catch {
      notify?.('Could not link the selected items. Please try again.');
    } finally { setSubmitting(false); }
  }, [selected, taskId, notify, onLinked]);

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="event-picker-dialog">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} onClick={onClose} />
      <div className="relative flex max-h-[80vh] w-full max-w-[460px] flex-col overflow-hidden rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]">
        <div className="flex items-center justify-between border-b border-[var(--n-line)] px-4 py-3">
          <h3 className="flex items-center gap-2 text-[14px] font-semibold text-[var(--n-ink)]"><CalendarClock size={16} className="text-[var(--n-accent)]" /> Link an event or meeting</h3>
          <button type="button" onClick={onClose} data-testid="event-picker-close" className="n-focus grid h-7 w-7 place-items-center rounded-lg text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
        </div>
        <div className="border-b border-[var(--n-line)] px-4 py-2.5">
          <div className="flex items-center gap-2 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas)] px-2.5 py-1.5">
            <Search size={14} className="text-[var(--n-muted-2)]" />
            <input data-testid="event-picker-search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
              placeholder="Search events & meetings…" className="w-full bg-transparent text-[13px] text-[var(--n-ink)] outline-none placeholder:text-[var(--n-muted-2)]" />
          </div>
          <p className="mt-1.5 text-[11px] text-[var(--n-muted-2)]">Showing items scheduled today or later.</p>
        </div>
        <div className="min-h-[160px] flex-1 overflow-y-auto n-scroll px-2 py-2">
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-[var(--n-muted)]"><Loader2 size={13} className="animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 px-2 py-8 text-center text-[12px] text-[var(--n-muted)]">
              <CalendarPlus size={22} className="text-[var(--n-muted-2)]" />
              No upcoming events or meetings{q ? ` for "${q}"` : ''}.
            </div>
          ) : (
            <ul className="space-y-1">
              {results.map((t) => {
                const key = `${t.target_type}::${t.target_id}`;
                const on = selected.has(key);
                return (
                  <li key={key}>
                    <button type="button" data-testid="event-picker-item" data-selected={on ? 'true' : 'false'} data-linked={t.linked ? 'true' : 'false'}
                      disabled={t.linked} onClick={() => toggle(key)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${t.linked ? 'cursor-default opacity-60' : on ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                      <span className={`grid shrink-0 place-items-center rounded border ${on || t.linked ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)]'}`} style={{ height: 18, width: 18 }}>
                        {(on || t.linked) && <Check size={12} strokeWidth={3} />}
                      </span>
                      <KindIcon kind={t.kind} size={14} className="shrink-0 text-[var(--n-accent)]" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{t.title}</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-[var(--n-muted)]">
                          <span className="capitalize">{t.kind}</span>
                          <span className="text-[var(--n-muted-2)]">·</span>
                          {dateLabel(t.date)}{t.start_time ? ` · ${t.start_time}` : ''}
                          {t.recurring && <span className="inline-flex items-center gap-0.5 text-[var(--n-accent)]"><Repeat size={10} />{recurShort(t.recurrence)}</span>}
                        </span>
                      </span>
                      {t.linked && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[var(--n-accent)]">Linked</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-[var(--n-line)] px-4 py-3">
          <span className="text-[12px] text-[var(--n-muted)]" data-testid="event-picker-selected-count">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Cancel</button>
            <button type="button" data-testid="event-picker-link-submit" onClick={submit} disabled={selected.size === 0 || submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-white transition-opacity disabled:opacity-50">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Link{selected.size > 0 ? ` ${selected.size}` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
